import * as yup from 'yup'
import { IntlShape, MessageDescriptor, defineMessages } from 'react-intl'
import { isEmpty } from 'lodash'

import { LETTERS_MASK } from 'utils/masks'
import { hasMinChar } from 'utils/string'
import { GTBookingV2PaymentProcessor } from 'gql/types.generated'

const emailMessages = defineMessages({
  required: {
    id: 'bookings.buyer.email.required',
    defaultMessage: 'The email is required'
  },
  max: {
    id: 'bookings.buyer.email.max',
    defaultMessage: 'The email can be up to 255 characters'
  },
  format: {
    id: 'bookings.buyer.email.format',
    defaultMessage: 'The format is incorrect'
  }
})

const phoneMessages = defineMessages({
  required: {
    id: 'bookings.buyer.phone.required',
    defaultMessage: 'The phone is required'
  },
  onlyDigits: {
    id: 'bookings.form.phone.onlyDigits',
    defaultMessage: 'The phone is digits only'
  },
  min: {
    id: 'bookings.form.phone.min',
    defaultMessage: 'The phone should have at least 10 numbers'
  },
  max: {
    id: 'bookings.form.phone.max',
    defaultMessage: 'The phone can be up to 20 numbers'
  }
})

const passengerFirstNameMessages = defineMessages({
  required: {
    id: 'bookings.passenger.firstName.required',
    defaultMessage: 'The first name is required'
  },
  max: {
    id: 'bookings.passenger.firstName.max',
    defaultMessage: 'The first name can be up to 255 characters'
  },
  min: {
    id: 'bookings.passenger.firstName.min',
    defaultMessage: 'The first name should have at least 2 characters'
  },
  text: {
    id: 'bookings.passenger.firstName.text',
    defaultMessage: 'The first name should not have special characters'
  }
})

const passengerLastNameMessages = defineMessages({
  required: {
    id: 'bookings.passenger.lastName.required',
    defaultMessage: 'The last name is required'
  },
  max: {
    id: 'bookings.passenger.lastName.max',
    defaultMessage: 'The last name can be up to 255 characters'
  },
  min: {
    id: 'bookings.passenger.lastName.min',
    defaultMessage: 'The last name should have at least 2 characters'
  },
  text: {
    id: 'bookings.passenger.lastName.text',
    defaultMessage: 'The last name should not have special characters'
  }
})

const last4Messages = defineMessages({
  required: {
    id: 'bookings.payment.last4.required',
    defaultMessage: 'Required'
  },
  min: {
    id: 'bookings.payment.last4.min',
    defaultMessage: '4 digits'
  }
})

const testIsRequired = (value?: string, isRequired = false): boolean => {
  if (isRequired) {
    return !isEmpty(value)
  }

  return true
}

export const schema = (
  intl: IntlShape,
  {
    isPhoneRequired,
    isEmailRequired,
    isPassengerRequired
  }: { isPhoneRequired: boolean; isEmailRequired: boolean; isPassengerRequired: boolean }
): TSFixMe => {
  const getNameSchema = (messages: {
    max: MessageDescriptor
    text: MessageDescriptor
    required: MessageDescriptor
    min: MessageDescriptor
  }): { optional: yup.StringSchema<string | undefined>; required: yup.SchemaOf<string> } => {
    const optional = yup
      .string()
      .max(255, intl.formatMessage(messages.max))
      .matches(LETTERS_MASK, intl.formatMessage(messages.text))
    const required = optional
      .test('has-min', intl.formatMessage(messages.min), hasMinChar)
      .required(intl.formatMessage(messages.required))
    return { optional, required }
  }

  const firstNameSchema = getNameSchema(passengerFirstNameMessages)
  const lastNameSchema = getNameSchema(passengerLastNameMessages)

  return yup.object().shape({
    buyer: yup.object().shape({
      email: yup
        .string()
        .test('is-email-required', intl.formatMessage(emailMessages.required), function (value) {
          return testIsRequired(value, isEmailRequired)
        })
        .max(255, intl.formatMessage(emailMessages.max))
        .email(intl.formatMessage(emailMessages.format)),
      phone: yup
        .string()
        .test('is-phone-required', intl.formatMessage(phoneMessages.required), function (value) {
          return testIsRequired(value, isPhoneRequired)
        })
        .test('has-min-phone', intl.formatMessage(phoneMessages.min), (value): TSFixMe => {
          if (!value) return true

          return value.length >= 10
        })
        .max(20, intl.formatMessage(phoneMessages.max))
        .matches(/^[0-9]*$/, intl.formatMessage(phoneMessages.onlyDigits))
    }),
    passengers: yup.array().of(
      yup.object().shape({
        firstName: isPassengerRequired ? firstNameSchema.required : firstNameSchema.optional,
        lastName: isPassengerRequired ? lastNameSchema.required : lastNameSchema.optional
      })
    ),
    payments: yup.array().of(
      yup.object().shape({
        processor: yup.string().nullable(),
        last4: yup
          .string()
          .nullable()
          .when('processor', {
            is: GTBookingV2PaymentProcessor.Clip,
            then: yup
              .string()
              .required(intl.formatMessage(last4Messages.required))
              .test('has-min-4', intl.formatMessage(last4Messages.min), (last4): TSFixMe => {
                last4 = last4 || ''
                last4 = last4.replace(/_/g, '')
                return last4.length >= 4
              })
          })
      })
    ),
    tickets: yup.array()
  })
}
